import { z } from 'zod';
export const HtmlContentUpdateSchema = z.object({
    updates: z
        .array(z.object({
        selector: z.string().describe(`
          A Cheerio selector for exactly ONE element, written bare (never put a tag name in front):
          - a content slot: "[data-updatable-title-1]"
          - the inner text leaf of a slot that wraps block children: "[data-updatable-title-2] p"
          - an authorized non-slot element: "[data-edit-ref="e7"]"
          - a link, for an href update: "[data-link-id="l3"]"
          data-updatable-*, data-edit-ref and data-link-id attributes are unique, so the attribute alone identifies
          the element. Classes and inline styles are not part of the HTML you are shown, so never select
          by them.

          Prohibited selectors:
          - :contains('text')
          - :has()
          - html
          - body
          - style
          - head
        `),
        attributeName: z.string().optional().describe(`If specified, the attribute name to update.
        Example: "href" or "style" or "alt" or "aria-label" or "title"`),
        attributeValue: z
            .string()
            .optional()
            .describe('If specified, the attribute value to update'),
        value: z.string().optional().describe(`
          The new inner HTML for the element: real copy, as plain text or with inline formatting
          (e.g. "my paragraph text", "Save <strong>20%</strong> today"). It replaces the element's whole
          content, so it is never empty or blank. A link kept in the value keeps its data-link-id
          (e.g. <a href="#" data-link-id="l3">), so its real URL is put back, on every copy of it.
        `),
    }))
        .describe('List of HTML content updates, each targeting a specific element selected by a CSS selector compatible with Cheerio.'),
});
export const EmailFlowItemSchema = z.object({
    subjectLine: z.string().describe('The subject line of the email'),
    preheaderText: z.string().describe('The preheader text of the email'),
    htmlUpdates: HtmlContentUpdateSchema,
});
export const EmailFlowWidgetSchemaKey = 'email-flow-widget:v1';
export const EmailFlowWidgetSchemaKind = 'email';
export const EmailFlowWidgetSchema = EmailFlowItemSchema.pick({
    subjectLine: true,
    preheaderText: true,
}).extend({
    htmlContent: z.string().describe('Final HTML content after updates'),
    campaignId: z
        .string()
        .nullable()
        .optional()
        .describe('KB file id of the ".campaign" brief used to generate this email (when one was selected). ' +
        'Carried so the publish flow can send the exact campaign used, immune to later selection changes.'),
    errors: z.array(z.string()).describe('List of errors encountered during processing'),
    warnings: z
        .array(z.string())
        .optional()
        .describe('List of warnings encountered during processing'),
    reasoningAndFocus: z
        .string()
        .optional()
        .nullable()
        .describe(`Explanation of which input elements (brand guidelines, past campaign data, user prompt, or QA results) had the highest impact on generating the email copy, and the specific reasons why they were most influential in shaping the content, tone, and structure of the output.`),
});
